import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { type InvoiceAiModelSettings } from './types';
import { zodInvoiceAiModelSettingsResponseSchema } from './validations';

/**
 * Fetches a single invoice AI model settings' details from the API.
 *
 * @param aiModelSettingsId - The ID of the invoice AI model settings to fetch.
 * @param options - Optional request options (e.g., abort signal).
 * @returns The invoice AI model settings data.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/get_invoice_ai_model_settings_details_vadmin_invoice_ai_model_settings__model_id__get
 */
export const fetchInvoiceAiModelSettings = async (
  aiModelSettingsId: UUID,
  options?: KyRequestOptions,
) => {
  const response = await vestigasApi
    .get(ENDPOINT.INVOICE_AI_MODEL.SETTINGS.GET(aiModelSettingsId), {
      ...options,
    })
    .json<InvoiceAiModelSettings>();

  // Validate the response and log any validation errors
  const validatedResponse =
    zodInvoiceAiModelSettingsResponseSchema.safeParse(response);

  if (validatedResponse?.error?.issues?.length) {
    log.error({
      message: 'AI model settings details API response has validation errors',
      error: {
        errors: validatedResponse?.error?.issues,
        errorMessage: validatedResponse?.error?.message,
        response,
      },
    });
  }

  return response;
};
